import { z } from "zod"

/**
 * Schema for creating/editing fish health checks.
 * Validation rules match backend constraints.
 */
export const fishHealthSchema = z.object({
  // Required fields
  feedType: z
    .string({ message: "Feed type is required" })
    .min(1, "Feed type is required")
    .max(255, "Feed type must be less than 255 characters"),

  averageWeight: z
    .number({ message: "Average weight is required" })
    .positive("Average weight must be greater than 0"),

  sampleSize: z
    .number({ message: "Sample size is required" })
    .int("Sample size must be a whole number")
    .positive("Sample size must be greater than 0"),

  checkedAt: z.date({ message: "Check date/time is required" }),

  // Optional fields
  mortalityCount: z
    .number()
    .int("Mortality count must be a whole number")
    .min(0, "Mortality count cannot be negative")
    .optional()
    .nullable(),

  parasiteCodes: z.array(z.string()).optional(),

  behaviorObservations: z
    .string()
    .max(2000, "Observations must be less than 2000 characters")
    .optional()
    .nullable(),

  physicalSymptoms: z
    .string()
    .max(2000, "Symptoms must be less than 2000 characters")
    .optional()
    .nullable(),

  treatmentApplied: z
    .string()
    .max(1000, "Treatment must be less than 1000 characters")
    .optional()
    .nullable(),

  veterinarianNotes: z
    .string()
    .max(2000, "Notes must be less than 2000 characters")
    .optional()
    .nullable(),

  imageIds: z.array(z.string()).max(5, "Maximum 5 images allowed").optional(),
})

export type FishHealthFormData = z.infer<typeof fishHealthSchema>

/**
 * Convert form data to API request format.
 */
export function toApiRequest(data: FishHealthFormData) {
  return {
    feedType: data.feedType,
    averageWeight: data.averageWeight,
    sampleSize: data.sampleSize,
    checkedAt: data.checkedAt.toISOString(),
    ...(data.mortalityCount != null && { mortalityCount: data.mortalityCount }),
    ...(data.parasiteCodes && data.parasiteCodes.length > 0 && {
      parasiteCode: data.parasiteCodes[0], // API takes single parasite code
      parasiteName: data.parasiteCodes.join(", "), // Store all as comma-separated
    }),
    ...(data.behaviorObservations && { behaviorObservations: data.behaviorObservations }),
    ...(data.physicalSymptoms && { physicalSymptoms: data.physicalSymptoms }),
    ...(data.treatmentApplied && { treatmentApplied: data.treatmentApplied }),
    ...(data.veterinarianNotes && { veterinarianNotes: data.veterinarianNotes }),
    ...(data.imageIds && data.imageIds.length > 0 && { imageIds: data.imageIds }),
  }
}
